// A typed headcount box beside Available MP / day on the Crew Schedule: Temps
// active (#584) and Subcontractors (#688). What each figure is lives in
// typedHeadcounts.ts; this is the box they share.
//
// ⚠️ TYPED, NOT DERIVED. The manpower lead enters it by hand, like the Gap
// Analysis supply boxes, and nothing on the board computes, checks or spends it.
// It is one current figure, not a count week by week. That trade was chosen on
// #584, as was keeping the figure out of everything else on the page (grid,
// Headroom, Gap Analysis, Excel) and off paper: the whole box is `mp-noprint`.
// Don't wire it into any of them without going back to the issue.
//
// ⚠️ THE CAPTION SAYS WHETHER THE FIGURE IS COUNTED INSIDE AVAILABLE MP. Temps
// (#584) and subcontractors (#688) both are. For a figure inside it, the API
// refuses a count that, with the other figures inside it, exceeds the stored
// Available MP. If Available MP is lowered below that afterwards, or another
// figure is raised, the box says so here rather than blocking that edit.

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { manpowerApi } from '@/lib/apiClient';
import { fmtInstantDate } from '@/lib/dates';
import { isDefiniteRejection } from '@/lib/httpErrors';
import { invalidateManpowerConfigQueries } from '@/lib/queryClient';
import { TYPED_HEADCOUNTS, availableMpRelation, type TypedHeadcount, type TypedHeadcountFigure } from './typedHeadcounts';

/**
 * A stored headcount, or null when there is none to read. Mirrors the API's
 * parseHeadcount (analysis-metrics.ts), which setConfig uses for BOTH sides of
 * the Available MP check, so the box treats as unknown exactly what the API does.
 */
export function parseHeadcount(raw: string | null | undefined): number | null {
  if (raw === null || raw === undefined) return null;
  const t = String(raw).trim();
  return /^\d{1,4}$/.test(t) ? Number(t) : null;
}

/**
 * Why a typed value isn't a headcount, or null when it is.
 *
 * ⚠️ THE SHAPE ONLY, NOT THE CEILING. "No more than Available MP" is the API's
 * to enforce, against the figure stored NOW. This page's Available MP can be
 * stale for as long as it stays open, so a ceiling checked here would refuse a
 * count that someone else's raise has made valid, without the API ever seeing
 * it (Codex, #584). Sent instead, an over-ceiling count comes back refused with
 * the API's reason and the refetch in onError brings the badge up to date.
 */
export function headcountDraftError(raw: string): string | null {
  return parseHeadcount(raw.trim()) === null ? 'Enter a whole number of people, from 0 to 9999.' : null;
}

/**
 * What to say when a save fails.
 *
 * ⚠️ Read the ApiError, not `err.response`. apiClient's interceptor replaces
 * every Axios error before any caller sees it (lib/httpErrors.ts), so the
 * server's text is `err.message` and there is no `response` left. A definite
 * refusal is the API naming its rule, typically that Available MP was changed
 * elsewhere, and is shown as given. A dropped connection or a 5xx says nothing
 * about the value, and may even have saved, so it gets the generic line.
 */
export function saveFailureMessage(err: unknown): string {
  if (isDefiniteRejection(err) && err instanceof Error && err.message) return err.message;
  return "Couldn't save. Try again.";
}

/** A refused entry: the API's message for a failed save, or null when refused here. */
export type Rejected = { value: string; serverMessage: string | null };

/**
 * What a refused entry still has to say, or null once it has nothing true to.
 *
 * Nothing once the stored figure IS the refused value: a dropped connection or
 * a 5xx can arrive after the write landed, and the refetch that follows shows
 * it, so "Couldn't save" beside the saved number would be false. Otherwise the
 * API's message as given, or the shape refusal made here.
 */
export function refusalMessage(rejected: Rejected | null, count: number | null): string | null {
  if (rejected === null) return null;
  if (count !== null && Number(rejected.value) === count) return null;
  return rejected.serverMessage ?? headcountDraftError(rejected.value);
}

/**
 * The box's state for a value the API has just accepted. Stamped with this
 * browser's clock, which the post-save refetch replaces with the server's.
 */
export function savedTypedHeadcount(value: string, now: Date = new Date()): { count: number; updatedAt: string } {
  return { count: Number(value.trim()), updatedAt: now.toISOString() };
}

/** The line under the label: inside Available MP or not, plus how fresh the figure is. */
export function headcountCaption(figure: TypedHeadcountFigure, count: number | null, updatedAt: string | null): string {
  const relation = availableMpRelation(figure);
  if (count === null) return `${relation} · not entered yet`;
  return updatedAt ? `${relation} · updated ${fmtInstantDate(updatedAt)}` : relation;
}

/**
 * The flag for a figure inside Available MP whose count, with the other figures
 * inside it, is more than Available MP: Available MP was lowered afterwards, or
 * one of the others was raised. Null when there's nothing to flag, including for
 * a figure on top of Available MP, and whenever Available MP isn't a stored
 * figure (`null`, see the `availableMp` prop).
 */
export function overAvailableMessage(
  figure: TypedHeadcountFigure,
  count: number | null,
  others: ReadonlyArray<{ label: string; count: number }>,
  availableMp: number | null,
): string | null {
  if (!figure.inAvailableMp || count === null || availableMp === null) return null;
  if (others.reduce((sum, o) => sum + o.count, count) <= availableMp) return null;
  if (others.length === 0) return `More than Available MP (${availableMp}). Check both.`;
  return `With ${others.map((o) => `${o.label} (${o.count})`).join(' and ')}, more than Available MP (${availableMp}). Check them.`;
}

/** The query every box makes for a figure, shared so a figure is cached once. */
function figureQuery(figure: TypedHeadcountFigure) {
  // Refetched whenever the window regains focus, unlike the app's default (off).
  // It's one hand-kept figure, often updated from another tab or device, and a
  // box reopened on the old count would start its draft there (Codex, #584).
  // 'always' rather than true: a focus within the two-minute staleTime would
  // otherwise be skipped. Deliberately NOT polling or a versioned write: the
  // rest of the board doesn't refresh across sessions either, and guarding
  // concurrent writers needs a server-side version check on mp_config.
  return { queryKey: figure.queryKey, queryFn: figure.fetch, refetchOnWindowFocus: 'always' as const };
}

export default function TypedHeadcountBox({ figure, availableMp, canEdit }: {
  figure: TypedHeadcountFigure;
  /** Available MP / day as this page knows it, used only to FLAG a stored
   *  count above it, and only for a figure inside it. Saving is never refused
   *  on it; the API enforces the ceiling against the current stored figure
   *  (see headcountDraftError). Null whenever the page's figure is only the 144
   *  fallback (config unloaded or failed, or no valid stored row), and then
   *  nothing is flagged. */
  availableMp: number | null;
  /** Same gate as Available MP: full manpower editors only. */
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const { data, isError } = useQuery<TypedHeadcount>(figureQuery(figure));
  const count = data?.count ?? null;
  // ⚠️ Not loaded is UNKNOWN, not "not entered yet", and it isn't editable.
  // Opening the box without the stored figure would start from an empty draft,
  // and saving would replace a number nobody on this page has seen.
  const loaded = data !== undefined;
  const editable = canEdit && loaded;

  // The other figures sharing Available MP's ceiling with this one, if it has
  // one: for temps, the subcontractors, and the other way round. The same
  // queries the boxes beside it make, so nothing is fetched twice.
  const sharers = figure.inAvailableMp
    ? TYPED_HEADCOUNTS.filter((f) => f.inAvailableMp && f.key !== figure.key)
    : [];
  const sharerResults = useQueries({ queries: sharers.map(figureQuery) });
  const others = sharers.flatMap((f, i) => {
    const n = (sharerResults[i]?.data as TypedHeadcount | undefined)?.count;
    return n === null || n === undefined ? [] : [{ label: f.label, count: n }];
  });

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  // The last refused entry. A refusal from the API carries its own message,
  // which quotes the STORED Available MP and stays until the next edit, since
  // it is the only place that figure may yet appear on this page. Either kind
  // goes quiet if the stored figure turns out to be the refused value. See
  // refusalMessage.
  const [rejected, setRejected] = useState<Rejected | null>(null);
  const error = refusalMessage(rejected, count);
  const inputRef = useRef<HTMLInputElement>(null);
  // Enter and Escape both unmount the focused input, and a browser may fire
  // blur on removal. Without this, that blur would commit again: a second save
  // after Enter, and a save instead of a cancel after Escape.
  const blurHandled = useRef(false);

  useEffect(() => { if (editing) { inputRef.current?.focus(); inputRef.current?.select(); } }, [editing]);

  const save = useMutation({
    mutationFn: (value: string) => manpowerApi.updateConfig(figure.key, value),
    // Every mp_config cache, not just this page's: Admin → Config lists this
    // row under a different key (see invalidateManpowerConfigQueries).
    //
    // ⚠️ THE SAVED VALUE GOES INTO THIS BOX'S CACHE FIRST. A refetch that
    // fails is swallowed (TanStack resolves invalidation regardless), so
    // waiting on it alone could unlock the box still holding the previous
    // count, ready to be saved back over the one just stored (Codex, #584).
    // Written here, the box shows what the API accepted whatever the refetch
    // does, and the refetch then only corrects the stamp to the server's.
    //
    // RETURNED as well, so the save stays pending (and the edit control
    // locked, see `save.isPending`) until that refetch settles.
    onSuccess: (_res, value) => {
      setRejected(null);
      qc.setQueryData(figure.queryKey, savedTypedHeadcount(value));
      return invalidateManpowerConfigQueries(qc);
    },
    // The API's own message names the rule that refused it: typically that,
    // with the other figure sharing Available MP, the total would be over it,
    // or that Available MP or the other figure was changed in another tab since
    // this page loaded. Refetch every figure so the badge and the box beside
    // this one catch up with that message, and hold the box until they have
    // (as above).
    onError: (e: unknown, value) => {
      setRejected({ value, serverMessage: saveFailureMessage(e) });
      return invalidateManpowerConfigQueries(qc);
    },
  });

  function start() {
    blurHandled.current = false;
    setDraft(count === null ? '' : String(count));
    setRejected(null);
    setEditing(true);
  }

  function cancel() {
    blurHandled.current = true;
    setEditing(false);
  }

  function commit() {
    if (blurHandled.current) return;
    blurHandled.current = true;
    setEditing(false);
    const t = draft.trim();
    // Blurring an untouched or emptied box is a cancel, not a save of nothing.
    if (t === '' || (count !== null && t === String(count))) return;
    if (headcountDraftError(t)) { setRejected({ value: t, serverMessage: null }); return; }
    save.mutate(t);
  }

  // Not blocked, since the edit that caused it belongs to a different box;
  // flagged so none of the numbers is trusted until someone looks.
  const overAvailable = overAvailableMessage(figure, count, others, availableMp);
  const loadFailed = !loaded && isError;
  const caption = error
    ?? (loadFailed ? "Couldn't load this figure. Refresh to try again."
      : !loaded ? availableMpRelation(figure)
      : overAvailable ?? headcountCaption(figure, count, data.updatedAt));
  const captionAlert = error !== null || overAvailable !== null || loadFailed;

  const shown = count === null ? '—' : String(count);
  const numberStyle: React.CSSProperties = {
    fontSize: 22, fontWeight: 700, minWidth: 32, textAlign: 'center', fontVariantNumeric: 'tabular-nums',
    color: count === null ? 'var(--ew-n-400)' : 'var(--ew-dark)',
  };

  return (
    // ⚠️ mp-noprint: the typed boxes appear on screen only, never in a printout
    // of the Crew Schedule (#584, Codex). The page's print rules hide this class.
    <div
      className="mp-noprint"
      title={figure.title}
      style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--ew-n-50)', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '5px 16px' }}
    >
      <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
        <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ew-n-700)' }}>
          {figure.label}
        </span>
        {/* Wraps, within a cap. The normal caption fits on one line, but a
            refusal from the API is a full sentence and on one line would push
            the header past a phone's width. */}
        <span role={captionAlert ? 'alert' : undefined} style={{ fontSize: 10, color: captionAlert ? 'var(--ew-danger)' : 'var(--ew-n-500)', maxWidth: 240, overflowWrap: 'anywhere' }}>
          {caption}
        </span>
      </span>
      {editing && editable ? (
        <>
          <input
            ref={inputRef}
            type="number" min={0} max={9999}
            aria-label={figure.label}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              else if (e.key === 'Escape') cancel();
            }}
            style={{ width: 60, fontSize: 22, fontWeight: 700, textAlign: 'center', border: '2px solid var(--ew-n-400)', borderRadius: 'var(--ew-r-md)', padding: '1px 4px', color: 'var(--ew-dark)', background: 'var(--ew-white)', fontFamily: 'var(--ew-font)' }}
          />
        </>
      ) : editable ? (
        <span
          role="button"
          tabIndex={0}
          onClick={save.isPending ? undefined : start}
          onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !save.isPending) { e.preventDefault(); start(); } }}
          title={figure.editHint}
          style={{ ...numberStyle, display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', borderBottom: '1px dashed var(--ew-n-400)' }}
        >
          {shown}
          <span aria-hidden style={{ fontSize: 12, opacity: 0.7 }}>✎</span>
        </span>
      ) : (
        <span style={numberStyle}>{shown}</span>
      )}
    </div>
  );
}
